import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  InputAdornment,
  Paper,
  CircularProgress,
  Box,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import { Checkbox, FormControl, InputLabel, ListItemText, MenuItem, OutlinedInput, Select } from '@mui/material';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import { LatLng } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import InvoiceService from '../services/invoice.service';
import ProductService from '../services/product.service';
import { requestService } from '../services/requestService';
import { NumberTextField } from './common';
import { REQUEST_REASONS } from '../constants/requestReasons';

// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Default services that can be selected in invoice creation
const DEFAULT_SERVICES = [
  'Extra km',
  ...REQUEST_REASONS,
];

interface LineItem {
  tempId: string;
  product?: any;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
}

interface CreateInvoiceModalProps {
  open: boolean;
  onClose: () => void;
  request: any;
  onSuccess: () => void;
}

const CreateInvoiceModal: React.FC<CreateInvoiceModalProps> = ({
  open,
  onClose,
  request,
  onSuccess,
}) => {
  const [products, setProducts] = useState<any[]>([]);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  const [formData, setFormData] = useState({
    invoiceDate: new Date().toISOString().split('T')[0],
    dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    taxRate: 0.05,
    other: 0,
    notes: '',
    reasons: [] as string[],
    toLocationLatitude: null as number | null,
    toLocationLongitude: null as number | null,
    toLocationText: '',
  });
  
  const [toLocationMapCenter, setToLocationMapCenter] = useState<[number, number]>([23.5859, 58.4059]); // Default to Muscat, Oman

  useEffect(() => {
    if (open) {
      loadProducts();
      // Reset state when modal opens
      setLineItems([]);
      setError('');
      setSuccess('');
      const attrs = request?.attributes || request;
      // Pre-fill "to" location if request already has it
      setFormData(prev => ({
        ...prev,
        reasons: [],
        toLocationLatitude: attrs?.toLocationLatitude || null,
        toLocationLongitude: attrs?.toLocationLongitude || null,
        toLocationText: attrs?.toLocationText || '',
      }));
      // Set map center to existing "to" location or default
      if (attrs?.toLocationLatitude && attrs?.toLocationLongitude) {
        setToLocationMapCenter([attrs.toLocationLatitude, attrs.toLocationLongitude]);
      } else if (attrs?.locationLatitude && attrs?.locationLongitude) {
        setToLocationMapCenter([attrs.locationLatitude, attrs.locationLongitude]);
      }
    }
  }, [open, request]);

  const loadProducts = async () => {
    try {
      const response = await ProductService.getAll({
        'filters[isActive][$eq]': true,
        'pagination[pageSize]': 100,
      });
      setProducts(response.data?.data || []);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const addLineItem = () => {
    const newItem: LineItem = {
      tempId: `temp-${Date.now()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0,
    };
    setLineItems([...lineItems, newItem]);
  };

  const removeLineItem = (tempId: string) => {
    setLineItems(lineItems.filter((item) => item.tempId !== tempId));
  };

  const updateLineItem = (tempId: string, field: keyof LineItem, value: any) => {
    setLineItems(
      lineItems.map((item) => {
        if (item.tempId === tempId) {
          const updated = { ...item, [field]: value };

          if (field === 'product' && value) {
            // If value is a string (default service or custom text), set description
            if (typeof value === 'string') {
              updated.description = value;
              updated.product = undefined; // Clear product if it's a string
            } else {
              // If value is a product object, set product and description
              updated.unitPrice = Number(value.attributes?.price || value.price || 0);
              updated.description = value.attributes?.name || value.name || '';
              updated.product = value;
            }
          }

          if (field === 'quantity' || field === 'unitPrice') {
            updated[field] = Number(value) || 0;
          }

          if (field === 'description') {
            updated[field] = value;
            // If description is set manually and it's not a product, clear product
            if (typeof value === 'string' && !item.product) {
              updated.product = undefined;
            }
          }

          // Calculate total
          updated.total = updated.quantity * updated.unitPrice;

          return updated;
        }
        return item;
      })
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (lineItems.length === 0) {
      setError('Please add at least one line item.');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');

      const attrs = request?.attributes || request;
      const requestId = request?.id || attrs?.id;
      const customer = attrs.customer?.data || attrs.customer;
      const insuranceClient = attrs.insuranceClient?.data || attrs.insuranceClient;

      let customerId = customer?.id;
      
      // If insurance client, use the createFromRequest endpoint
      if (!customerId && insuranceClient) {
        // Format items for the backend API
        const items = lineItems.map((item) => ({
          description: item.description || 'Service',
          quantity: item.quantity || 1,
          unitPrice: item.unitPrice || 0,
          product: item.product?.id || null,
        }));

        // Use the createFromRequest endpoint for insurance clients
        // Backend automatically updates request status to invoice_sent
        await requestService.createInvoiceFromRequest(
          requestId,
          items,
          formData.dueDate,
          formData.notes || `Invoice for request ${attrs?.requestNumber || requestId}`,
          formData.reasons,
          formData.toLocationLatitude,
          formData.toLocationLongitude,
          formData.toLocationText
        );

        setSuccess('Invoice created successfully!');
        setTimeout(() => {
          onSuccess();
          onClose();
        }, 1500);
        return;
      }

      if (!customerId) {
        setError('Customer not found for this request.');
        return;
      }

      // Regular customer invoice creation flow
      const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
      const tax = subtotal * formData.taxRate;
      const total = subtotal + tax + formData.other;

      // Create invoice (backend assigns INV-YYYY-MM-####)
      const invoiceResponse = await InvoiceService.create({
        invoiceNumber: '', // Backend auto-generates INV-YYYY-MM-####
        customer: customerId,
        invoiceDate: formData.invoiceDate,
        dueDate: formData.dueDate,
        subtotal,
        tax,
        discount: 0,
        total,
        paidAmount: 0,
        balanceDue: total,
        status: 'sent',
        invoiceType: 'customer',
        taxRate: formData.taxRate,
        other: formData.other,
        notes: formData.notes,
      });

      const invoiceId = invoiceResponse.data?.data?.id;

      if (!invoiceId) {
        setError('Failed to create invoice.');
        return;
      }

      // Create invoice items
      await Promise.all(
        lineItems.map(async (item) => {
          const productId = item.product?.id;

          // If no product selected, create invoice item without product relation
          if (!productId) {
            return InvoiceService.createItem({
              invoice: invoiceId,
              description: item.description,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              discount: 0,
              tax: 0,
              total: item.total,
            });
          }

          return InvoiceService.createItem({
            invoice: invoiceId,
            product: productId,
            description: item.description,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            discount: 0,
            tax: 0,
            total: item.total,
          });
        })
      );

      // Link invoice to request, update reasons, and update status to invoice_sent
      const updateData: any = {
        invoice: invoiceId,
        status: 'invoice_sent',
        invoiceSentAt: new Date().toISOString(),
      };
      
      // Add reasons if provided
      if (formData.reasons && formData.reasons.length > 0) {
        updateData.reasons = formData.reasons;
      }
      
      // Add "to" location if provided
      if (formData.toLocationLatitude !== null && formData.toLocationLongitude !== null) {
        updateData.toLocationLatitude = formData.toLocationLatitude;
        updateData.toLocationLongitude = formData.toLocationLongitude;
        updateData.toLocationText = formData.toLocationText || '';
      }
      
      await requestService.update(requestId, updateData);

      setSuccess('Invoice created successfully!');
      setTimeout(() => {
        onSuccess();
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Error creating invoice:', err);
      setError(err.response?.data?.error?.message || err.message || 'Failed to create invoice.');
    } finally {
      setLoading(false);
    }
  };

  // Map click handler component
  const MapClickHandler: React.FC<{
    onLocationSelect: (lat: number, lng: number) => void;
  }> = ({ onLocationSelect }) => {
    useMapEvents({
      click: (e) => {
        const { lat, lng } = e.latlng;
        onLocationSelect(lat, lng);
      },
    });
    return null;
  };

  const subtotal = lineItems.reduce((sum, item) => sum + item.total, 0);
  const tax = subtotal * formData.taxRate;
  const total = subtotal + tax + formData.other;

  const attrs = request?.attributes || request;
  const customer = attrs?.customer?.data?.attributes || attrs?.customer;
  const insuranceClient = attrs?.insuranceClient?.data?.attributes || attrs?.insuranceClient?.data || attrs?.insuranceClient;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Typography variant="h6" fontWeight="bold">
          Create Invoice for Request #{attrs?.requestNumber || request?.id}
        </Typography>
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Customer/Insurance Client Info */}
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                {insuranceClient ? 'Insurance Client Information' : 'Customer Information'}
              </Typography>
              {insuranceClient ? (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    {insuranceClient?.companyName || insuranceClient?.name || 'N/A'}
                  </Typography>
                  {insuranceClient?.phone && (
                    <Typography variant="body2" color="textSecondary">
                      Phone: {insuranceClient.phone}
                    </Typography>
                  )}
                  {insuranceClient?.email && (
                    <Typography variant="body2" color="textSecondary">
                      Email: {insuranceClient.email}
                    </Typography>
                  )}
                </>
              ) : (
                <>
                  <Typography variant="body1" fontWeight="bold">
                    {customer?.companyName || customer?.contactPerson || 'N/A'}
                  </Typography>
                  {customer?.phone && (
                    <Typography variant="body2" color="textSecondary">
                      Phone: {customer.phone}
                    </Typography>
                  )}
                </>
              )}
            </Paper>

            {/* Invoice Details */}
            <Stack direction="row" spacing={2}>
              <TextField
                fullWidth
                label="Invoice Date"
                type="date"
                value={formData.invoiceDate}
                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                size="small"
              />
              <TextField
                fullWidth
                label="Due Date"
                type="date"
                value={formData.dueDate}
                onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
                required
                size="small"
              />
            </Stack>

            {/* Line Items */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="subtitle1" fontWeight="bold">Items</Typography>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addLineItem}
                  size="small"
                >
                  Add Item
                </Button>
              </Stack>

              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell width="35%">Product / Description</TableCell>
                      <TableCell width="15%">Quantity</TableCell>
                      <TableCell width="20%">Unit Price</TableCell>
                      <TableCell width="20%">Total</TableCell>
                      <TableCell width="10%"></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lineItems.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography variant="body2" color="textSecondary">
                            No items added. Click "Add Item" to start.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      lineItems.map((item) => (
                        <TableRow key={item.tempId}>
                          <TableCell>
                            <Autocomplete
                              freeSolo
                              value={item.product || item.description || null}
                              onChange={(e, newValue) => {
                                updateLineItem(item.tempId, 'product', newValue);
                              }}
                              onInputChange={(e, newValue) => {
                                // Only update description if no product is selected and it's not a default service
                                if (!item.product && !DEFAULT_SERVICES.includes(newValue)) {
                                  updateLineItem(item.tempId, 'description', newValue);
                                }
                              }}
                              options={[...DEFAULT_SERVICES, ...products]}
                              getOptionLabel={(option) => {
                                if (typeof option === 'string') return option;
                                return option.attributes?.name || option.name || '';
                              }}
                              isOptionEqualToValue={(option, value) => {
                                if (typeof option === 'string' && typeof value === 'string') {
                                  return option === value;
                                }
                                if (typeof option === 'string' || typeof value === 'string') {
                                  return false;
                                }
                                return option?.id === value?.id;
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  placeholder="Select service or type custom"
                                  value={item.description}
                                />
                              )}
                            />
                          </TableCell>
                          <TableCell>
                            <NumberTextField
                              size="small"
                              value={item.quantity}
                              onChange={(e) =>
                                updateLineItem(item.tempId, 'quantity', e.target.value)
                              }
                              inputProps={{ min: 1, step: 1 }}
                              allowDecimal={false}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <NumberTextField
                              size="small"
                              value={item.unitPrice}
                              onChange={(e) =>
                                updateLineItem(item.tempId, 'unitPrice', e.target.value)
                              }
                              inputProps={{ min: 0, step: 0.01 }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">OMR</InputAdornment>
                                ),
                              }}
                              fullWidth
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="bold">
                              OMR {item.total.toFixed(3)}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeLineItem(item.tempId)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            {/* Totals */}
            <Paper sx={{ p: 2, backgroundColor: '#f9f9f9' }}>
              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Subtotal:</Typography>
                  <Typography fontWeight="bold">OMR {subtotal.toFixed(3)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between">
                  <Typography>Tax ({formData.taxRate * 100}%):</Typography>
                  <Typography fontWeight="bold">OMR {tax.toFixed(3)}</Typography>
                </Stack>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography>Other:</Typography>
                  <NumberTextField
                    size="small"
                    value={formData.other}
                    onChange={(e) =>
                      setFormData({ ...formData, other: parseFloat(e.target.value) || 0 })
                    }
                    InputProps={{
                      startAdornment: <InputAdornment position="start">OMR</InputAdornment>,
                    }}
                    sx={{ width: 150 }}
                  />
                </Stack>
                <Stack
                  direction="row"
                  justifyContent="space-between"
                  sx={{ mt: 2, pt: 2, borderTop: '2px solid #ddd' }}
                >
                  <Typography variant="h6">Total:</Typography>
                  <Typography variant="h6" fontWeight="bold">
                    OMR {total.toFixed(3)}
                  </Typography>
                </Stack>
              </Stack>
            </Paper>

            {/* Reasons Selection */}
            <FormControl fullWidth size="small">
              <InputLabel id="reasons-label">Reasons (Problems Fixed)</InputLabel>
              <Select
                labelId="reasons-label"
                id="reasons-select"
                multiple
                value={formData.reasons}
                onChange={(e) => setFormData({ ...formData, reasons: e.target.value as string[] })}
                input={<OutlinedInput label="Reasons (Problems Fixed)" />}
                renderValue={(selected) => (selected as string[]).join(', ')}
              >
                {REQUEST_REASONS.map((reason) => (
                  <MenuItem key={reason} value={reason}>
                    <Checkbox checked={formData.reasons.indexOf(reason) > -1} />
                    <ListItemText primary={reason} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* To Location Picker */}
            <Box>
              <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                To Location (Destination) {formData.reasons && formData.reasons.length > 0 ? '*' : ''}
                {formData.reasons && formData.reasons.length > 0 && ' (Required when adding reasons)'}
              </Typography>
              <Stack spacing={2}>
                {/* Map Picker */}
                <Box sx={{ height: 300, width: '100%', border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                  <MapContainer
                    center={toLocationMapCenter}
                    zoom={13}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapClickHandler
                      onLocationSelect={(lat, lng) => {
                        setFormData(prev => ({
                          ...prev,
                          toLocationLatitude: lat,
                          toLocationLongitude: lng,
                        }));
                        setToLocationMapCenter([lat, lng]);
                      }}
                    />
                    {formData.toLocationLatitude !== null && formData.toLocationLongitude !== null && (
                      <Marker position={[formData.toLocationLatitude, formData.toLocationLongitude]} />
                    )}
                  </MapContainer>
                </Box>

                {/* Display selected coordinates */}
                {formData.toLocationLatitude !== null && formData.toLocationLongitude !== null && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    Selected: {formData.toLocationLatitude.toFixed(6)}, {formData.toLocationLongitude.toFixed(6)}
                  </Typography>
                )}

                {/* Location Text */}
                <TextField
                  fullWidth
                  size="small"
                  label="Location Description (Optional)"
                  value={formData.toLocationText}
                  onChange={(e) => setFormData({ ...formData, toLocationText: e.target.value })}
                  placeholder="e.g., Muscat, near Al Khuwair"
                />

                {/* Button to get current location */}
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    if (navigator.geolocation) {
                      navigator.geolocation.getCurrentPosition(
                        (position) => {
                          const lat = position.coords.latitude;
                          const lng = position.coords.longitude;
                          setFormData(prev => ({
                            ...prev,
                            toLocationLatitude: lat,
                            toLocationLongitude: lng,
                          }));
                          setToLocationMapCenter([lat, lng]);
                        },
                        (error) => {
                          console.error('Error getting location:', error);
                          setError('Unable to get your current location');
                        }
                      );
                    } else {
                      setError('Geolocation is not supported by your browser');
                    }
                  }}
                >
                  Use My Current Location
                  </Button>
                </Stack>
              </Box>

            {/* Notes */}
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              size="small"
            />
          </Stack>
        </form>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={loading || lineItems.length === 0 || (formData.reasons && formData.reasons.length > 0 && (formData.toLocationLatitude === null || formData.toLocationLongitude === null))}
        >
          {loading ? 'Creating...' : 'Create Invoice'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateInvoiceModal;

